import _defineProperty from "@babel/runtime/helpers/defineProperty";
function _classPrivateFieldInitSpec(e, t, a) { _checkPrivateRedeclaration(e, t), t.set(e, a); }
function _checkPrivateRedeclaration(e, t) { if (t.has(e)) throw new TypeError("Cannot initialize the same private elements twice on an object"); }
function _classPrivateFieldGet(s, a) { return s.get(_assertClassBrand(s, a)); }
function _classPrivateFieldSet(s, a, r) { return s.set(_assertClassBrand(s, a), r), r; }
function _assertClassBrand(e, t, n) { if ("function" == typeof e ? e === t : e.has(t)) return arguments.length < 3 ? t : n; throw new TypeError("Private element is not present on this object"); }
import { DATA, DISPLAY_GROUPS } from '@wix/component-protocol/schema';
import { NativeActionsList } from '@wix/editor-platform-host-integration-apis';
import { ManifestComponentExtensionType } from './ManifestStore';
import { ManifestHelpers } from './helpers';
import { ACTION_NAME_TO_DATA_TYPE } from './constants';
import { getEditorPointerUniqueId } from './getEditorPointerUniqueId';
import { filterOOIInnerElementsForEditText } from './utils/filterOOIInnerElemntsForEditText';
import { shouldShowBuilderElementsPanel } from './utils/shouldShowBuilderElementsPanel';
var _manifestHelpers = /*#__PURE__*/new WeakMap();
export class ComponentsService {
  constructor(dm, hostAPI, manifestContextAPIProvider, manifestStore, panelStore, manifestLifecycle, editorPointerAPI, manifestDefaultValues) {
    this.dm = dm;
    this.hostAPI = hostAPI;
    this.manifestContextAPIProvider = manifestContextAPIProvider;
    this.manifestStore = manifestStore;
    this.panelStore = panelStore;
    this.manifestLifecycle = manifestLifecycle;
    this.editorPointerAPI = editorPointerAPI;
    this.manifestDefaultValues = manifestDefaultValues;
    _classPrivateFieldInitSpec(this, _manifestHelpers, void 0);
    _defineProperty(this, "editActions", {
      resolveDataItems: (actionName, editorPointer) => {
        if (actionName === NativeActionsList.settings) {
          return Object.values(this.get.data(editorPointer) ?? {});
        }
        if (actionName === NativeActionsList.manageMenu) {
          return Object.values(this.get.menuData(editorPointer) ?? {});
        }
        if (actionName === NativeActionsList.editImage || actionName === NativeActionsList.cropImage) {
          return Object.values(this.get.imageData(editorPointer) ?? {});
        }
        if (actionName === NativeActionsList.changeMedia) {
          return Object.values(this.get.mediaData(editorPointer) ?? {});
        }
        if (actionName === NativeActionsList.editText) {
          const extensionType = this.manifestStore.getExtensionTypeByEditorPointer(editorPointer);
          if (extensionType === ManifestComponentExtensionType.OOI) {
            const metadata = this.manifestStore.getMetadataByEditorPointer(editorPointer);
            return filterOOIInnerElementsForEditText(metadata.statics);
          }
        }
        const dataType = ACTION_NAME_TO_DATA_TYPE[actionName];
        if (dataType) {
          return Object.values(this.get.data(editorPointer) ?? {}).filter(item => (item == null ? void 0 : item.dataType) === dataType);
        }
        return [];
      },
      resolvePresetItems: (actionName, editorPointer) => {
        if (actionName === NativeActionsList.presets) {
          var _firstGroup$preset;
          const presets = this.get.presets(editorPointer);
          const resolvePresetDisplayGroups = () => {
            const presetsGroups = this.get.displayGroups(editorPointer, {
              filter: {
                groupType: DISPLAY_GROUPS.GROUP_TYPE.preset
              }
            });
            const filteredPresetsGroups = Object.fromEntries(Object.entries(presetsGroups).map(_ref => {
              let [key, presetsGroup] = _ref;
              if (!presetsGroup.preset) {
                return null;
              }
              const filteredPresetItems = (presetsGroup.preset.items ?? []).filter(presetKey => Object.keys(presets).includes(presetKey));
              if (filteredPresetItems.length > 0) {
                return [key, {
                  ...presetsGroup,
                  preset: {
                    ...presetsGroup.dataType,
                    items: filteredPresetItems
                  }
                }];
              }
              return null;
            }).filter(entry => entry !== null));
            return filteredPresetsGroups;
          };
          const categories = resolvePresetDisplayGroups();
          if (!Object.keys(categories).length) {
            return presets;
          }
          const firstGroup = Object.values(categories)[0];
          if (Object.keys(categories).length === 1 && !(firstGroup != null && (_firstGroup$preset = firstGroup.preset) != null && (_firstGroup$preset = _firstGroup$preset.items) != null && _firstGroup$preset.length)) {
            return {};
          }
          const categorizedPresetKeys = Object.values(categories).reduce((acc, category) => {
            var _category$preset;
            return [...acc, ...(((_category$preset = category.preset) == null ? void 0 : _category$preset.items) ?? [])];
          }, []);
          return Object.fromEntries(Object.entries(presets ?? {}).filter(_ref2 => {
            let [presetKey] = _ref2;
            return categorizedPresetKeys.includes(presetKey);
          }));
        }
        return {};
      },
      hasTemplates: editorPointer => {
        return this.hostAPI.editor.templates.hasTemplates(editorPointer);
      },
      applyDisplayFilters: (editorPointer, actions) => {
        return this.getResolver(editorPointer).applyDisplayFilters(actions, 'actions');
      }
    });
    _defineProperty(this, "behaviors", {
      getValue: (editorPointer, behaviorName) => {
        var _manifest$behaviors;
        const manifest = this.manifestStore.getManifestByEditorPointer(editorPointer);

        // This is a temporary solution to handle top level elements until the manifest does not have the default behaviors
        // TODO: remove this once the manifest has the default behaviors for top level elements
        if (_classPrivateFieldGet(_manifestHelpers, this).isTopLevelElement(editorPointer) && (behaviorName === 'selectable' || behaviorName === 'removable')) {
          return true;
        }
        if (!manifest || !_classPrivateFieldGet(_manifestHelpers, this).isInnerElement(manifest)) {
          return null;
        }
        return ((_manifest$behaviors = manifest.behaviors) == null ? void 0 : _manifest$behaviors[behaviorName]) ?? null;
      }
    });
    _defineProperty(this, "get", {
      getAll: () => {
        return this.manifestStore.getAll();
      },
      editActions: editorPointer => {
        return this.getResolver(editorPointer).getActionDefinitions();
      },
      customActions: editorPointer => {
        return this.getResolver(editorPointer).getCustomActionDefinitions();
      },
      displayGroups: (editorPointer, options) => {
        return this.getResolver(editorPointer).getDisplayGroupDefinitions(options);
      },
      style: editorPointer => {
        return this.getResolver(editorPointer).getStyleDefinitions();
      },
      data: editorPointer => {
        return this.getResolver(editorPointer).getDataDefinitions();
      },
      help: editorPointer => {
        const manifest = this.manifestStore.getManifestByEditorPointer(editorPointer);
        return (manifest == null ? void 0 : manifest.helpArticle) ?? null;
      },
      interactions: editorPointer => {
        const manifest = this.manifestStore.getManifestByEditorPointer(editorPointer);
        if (!manifest || _classPrivateFieldGet(_manifestHelpers, this).isInnerElement(manifest)) {
          return null;
        }
        return (manifest == null ? void 0 : manifest.interactions) ?? null;
      },
      mediaData: editorPointer => {
        const mediaDataTypes = [DATA.DATA_TYPE.video, DATA.DATA_TYPE.image, DATA.DATA_TYPE.audio, DATA.DATA_TYPE.vectorArt];
        const manifest = this.manifestStore.getManifestByEditorPointer(editorPointer);
        const data = (manifest == null ? void 0 : manifest.data) ?? null;
        if (!data) {
          return null;
        }
        const filteredDataEntries = Object.entries(data).filter(_ref3 => {
          let [_key, item] = _ref3;
          return (item == null ? void 0 : item.dataType) && mediaDataTypes.includes(item.dataType);
        });
        if (filteredDataEntries.length === 0) {
          return null;
        }
        return Object.fromEntries(filteredDataEntries);
      },
      imageData: editorPointer => {
        const manifest = this.manifestStore.getManifestByEditorPointer(editorPointer);
        const data = (manifest == null ? void 0 : manifest.data) ?? null;
        if (!data) {
          return null;
        }
        const filteredImageDataEntries = Object.entries(data).filter(_ref4 => {
          let [_key, item] = _ref4;
          return (item == null ? void 0 : item.dataType) === DATA.DATA_TYPE.image;
        });
        if (filteredImageDataEntries.length === 0) {
          return null;
        }
        return Object.fromEntries(filteredImageDataEntries);
      },
      containerData: editorPointer => {
        const manifest = this.manifestStore.getManifestByEditorPointer(editorPointer);
        const data = (manifest == null ? void 0 : manifest.data) ?? null;
        if (!data) {
          return null;
        }
        const filteredContainerDataEntries = Object.entries(data).filter(_ref5 => {
          let [_key, item] = _ref5;
          return (item == null ? void 0 : item.dataType) === DATA.DATA_TYPE.container;
        });
        if (filteredContainerDataEntries.length === 0) {
          return null;
        }
        return Object.fromEntries(filteredContainerDataEntries);
      },
      menuData: editorPointer => {
        const manifest = this.manifestStore.getManifestByEditorPointer(editorPointer);
        const data = (manifest == null ? void 0 : manifest.data) ?? null;
        if (!data) {
          return null;
        }
        const filteredMenuDataEntries = Object.entries(data).filter(_ref6 => {
          let [_key, item] = _ref6;
          return (item == null ? void 0 : item.dataType) === DATA.DATA_TYPE.menuItems;
        });
        if (filteredMenuDataEntries.length === 0) {
          return null;
        }
        return Object.fromEntries(filteredMenuDataEntries);
      },
      presets: editorPointer => {
        return this.getResolver(editorPointer).getPresetDefinitions();
      },
      displayName: editorPointer => {
        const manifest = this.manifestStore.getManifestByEditorPointer(editorPointer);
        return (manifest == null ? void 0 : manifest.displayName) ?? null;
      },
      selector: editorPointer => {
        const manifest = this.manifestStore.getManifestByEditorPointer(editorPointer);
        return (manifest == null ? void 0 : manifest.selector) ?? null;
      },
      archetype: editorPointer => {
        const manifest = this.manifestStore.getManifestByEditorPointer(editorPointer);
        return (manifest == null ? void 0 : manifest.archetype) ?? null;
      },
      defaultDataValue: (editorPointer, dataItemId) => {
        return this.manifestDefaultValues.getDefaultDataValueByEditorPointer(editorPointer, dataItemId);
      },
      defaultStyleValue: (editorPointer, styleItemId) => {
        return this.manifestDefaultValues.getDefaultStyleValueByEditorPointer(editorPointer, styleItemId);
      },
      defaultCustomStyleValue: (editorPointer, styleItemId) => {
        return this.manifestDefaultValues.getDefaultCustomCssPropertyValueByEditorPointer(editorPointer, styleItemId);
      }
    });
    _defineProperty(this, "layout", {
      getValue: (editorPointer, layoutProperty) => {
        const layoutDefinitions = this.getResolver(editorPointer).getLayoutDefinitions();
        return (layoutDefinitions == null ? void 0 : layoutDefinitions[layoutProperty]) ?? null;
      }
    });
    _defineProperty(this, "states", {
      getStateDefinitions: editorPointer => {
        return this.getResolver(editorPointer).getStateDefinitions();
      },
      getState: editorPointer => {
        const context = this.manifestContextAPIProvider.getContext(editorPointer);
        return context.query.getState();
      },
      setState: (editorPointer, state) => {
        const context = this.manifestContextAPIProvider.getContext(editorPointer);
        return context.query.setState(state);
      },
      reconcileStatesOnComponentSelection: selectedComponentRefs => {
        const stateHandler = this.manifestContextAPIProvider.getManifestQueryAPI().state;
        const selectedEditorPointers = selectedComponentRefs.map(compRef => this.editorPointerAPI.buildEditorPointer(compRef));
        const selectedPointerKeys = new Set(selectedEditorPointers.map(pointer => getEditorPointerUniqueId(pointer)));
        const states = stateHandler.getGlobalStates();
        states.forEach(stateEntry => {
          const {
            editorPointer,
            type
          } = stateEntry;
          const editorPointerKey = getEditorPointerUniqueId(editorPointer);
          if (selectedPointerKeys.has(editorPointerKey)) {
            return;
          }
          const componentHierarchy = [editorPointer, ...this.getChildren(editorPointer, true)];
          const isWithinHierarchy = componentHierarchy.some(hierarchyPointer => {
            const hierarchyPointerKey = getEditorPointerUniqueId(hierarchyPointer);
            return selectedPointerKeys.has(hierarchyPointerKey);
          });
          if (!isWithinHierarchy) {
            stateHandler.setState(editorPointer, type, null);
          }
        });
      },
      activateVisibilityState: editorPointer => {
        const context = this.manifestContextAPIProvider.getContext(editorPointer);
        return context.query.activateVisibilityState();
      }
    });
    _defineProperty(this, "utilities", {
      isBuilderComponentType: compType => _classPrivateFieldGet(_manifestHelpers, this).isBuilderComponentType(compType)
    });
    _defineProperty(this, "isBoxLike", editorPointer => {
      var _this$manifestStore$g;
      const children = this.dm.components.getChildren(this.editorPointerAPI.getDocumentPointer(editorPointer));

      // Return false immediately if there isn't exactly one child.
      if (children.length !== 1) {
        return false;
      }
      const childPointer = this.editorPointerAPI.buildEditorPointer(children[0]);
      const manifestData = (_this$manifestStore$g = this.manifestStore.getManifestByEditorPointer(childPointer)) == null ? void 0 : _this$manifestStore$g.data;

      // If there's no manifest data, it can't be box-like.
      if (!manifestData) {
        return false;
      }

      // The component is "box-like" only if its single child
      // has exactly one property of the container data type.
      return Object.keys(manifestData).length === 1 && Object.values(manifestData)[0].dataType === DATA.DATA_TYPE.container;
    });
    _classPrivateFieldSet(_manifestHelpers, this, new ManifestHelpers(this.dm, this.editorPointerAPI));
  }
  getContext(editorPointer) {
    return this.manifestContextAPIProvider.getContext(editorPointer);
  }
  getResolver(editorPointer) {
    return this.getContext(editorPointer).resolver;
  }
  getPanel(editorPointer, actionName) {
    return this.panelStore.getPanelByEditorPointer(editorPointer, actionName);
  }
  async loadAppManifestAndPanels(appData) {
    return this.manifestLifecycle.handleApp(appData);
  }
  getElementsData(editorPointer) {
    const emptyElementsData = {
      extensionType: ManifestComponentExtensionType.UNKNOWN,
      showElementsPanel: false,
      manifest: undefined,
      metadata: null
    };
    const extensionType = this.manifestStore.getExtensionTypeByEditorPointer(editorPointer);
    const manifest = this.manifestStore.getManifestByEditorPointer(editorPointer);
    if (extensionType === ManifestComponentExtensionType.OOI) {
      var _metadata$statics;
      const metadata = this.manifestStore.getMetadataByEditorPointer(editorPointer);
      return {
        showElementsPanel: !!(metadata != null && (_metadata$statics = metadata.statics) != null && (_metadata$statics = _metadata$statics.elementsPanel) != null && _metadata$statics.length),
        extensionType,
        metadata,
        manifest
      };
    }
    if (extensionType === ManifestComponentExtensionType.BUILDER) {
      return {
        showElementsPanel: shouldShowBuilderElementsPanel(editorPointer, {
          getStyle: pointer => this.get.style(pointer),
          getBehaviorValue: (pointer, key) => this.behaviors.getValue(pointer, key),
          getChildren: (pointer, isRecursive) => this.getChildren(pointer, isRecursive)
        }),
        extensionType,
        manifest,
        metadata: null
      };
    }
    return emptyElementsData;
  }
  /**
   * NOTE method will return children elements filtered by display filters
   */
  getChildrenEditorPointers(editorPointer) {
    const children = [];
    const collect = api => {
      api.getChildren().forEach(child => {
        const childEditorPointer = child.getEditorPointer();
        if (childEditorPointer) {
          children.push(childEditorPointer);
          collect(child);
        }
      });
    };
    collect(this.getContext(editorPointer));
    return children;
  }
  getChildren(editorPointer, isRecursive) {
    const manifest = this.manifestStore.getManifestByEditorPointer(editorPointer);
    return _classPrivateFieldGet(_manifestHelpers, this).structure.getChildren(editorPointer, manifest, {
      isRecursive
    });
  }
  getAncestors(editorPointer) {
    return _classPrivateFieldGet(_manifestHelpers, this).structure.getAncestors(editorPointer);
  }
  getActionableElements(editorPointer, actionName, config) {
    if (config === void 0) {
      config = {
        recursive: false
      };
    }
    const manifest = this.manifestStore.getManifestByEditorPointer(editorPointer);
    return _classPrivateFieldGet(_manifestHelpers, this).structure.getActionableElements(editorPointer, manifest, actionName, config);
  }
  getPublicManifestAPI() {
    return {
      behaviors: {
        is: {
          removable: editorPointer => this.behaviors.getValue(editorPointer, 'removable') ?? false,
          selectable: editorPointer => this.behaviors.getValue(editorPointer, 'selectable') ?? false
        }
      },
      get: {
        getAll: () => this.get.getAll(),
        displayName: editorPointer => this.get.displayName(editorPointer),
        selector: editorPointer => this.get.selector(editorPointer),
        style: editorPointer => this.get.style(editorPointer),
        presets: editorPointer => this.get.presets(editorPointer),
        displayGroups: (editorPointer, options) => this.get.displayGroups(editorPointer, options),
        data: editorPointer => this.get.data(editorPointer),
        help: editorPointer => this.get.help(editorPointer),
        interactions: editorPointer => this.get.interactions(editorPointer),
        getMediaData: editorPointer => this.get.mediaData(editorPointer),
        editActions: editorPointer => this.get.editActions(editorPointer),
        customActions: editorPointer => this.get.customActions(editorPointer),
        archetype: editorPointer => this.get.archetype(editorPointer),
        containerData: editorPointer => this.get.containerData(editorPointer),
        defaultData: (editorPointer, dataItemId) => this.get.defaultDataValue(editorPointer, dataItemId),
        defaultStyle: (editorPointer, styleItemId) => this.get.defaultStyleValue(editorPointer, styleItemId)
      },
      layout: {
        get: {
          resizeDirection: editorPointer => this.layout.getValue(editorPointer, 'resizeDirection'),
          contentResizeDirection: editorPointer => this.layout.getValue(editorPointer, 'contentResizeDirection'),
          contentFill: editorPointer => this.layout.getValue(editorPointer, 'contentFill')
        },
        is: {
          stretchingDisabled: editorPointer => this.layout.getValue(editorPointer, 'disableStretching') ?? null,
          positioningDisabled: editorPointer => this.layout.getValue(editorPointer, 'disablePositioning') ?? null,
          rotationDisabled: editorPointer => this.layout.getValue(editorPointer, 'disableRotation') ?? null
        }
      },
      states: {
        getStateDefinitions: editorPointer => this.states.getStateDefinitions(editorPointer),
        getState: editorPointer => this.states.getState(editorPointer),
        setState: (editorPointer, stateKey) => this.states.setState(editorPointer, stateKey),
        activateVisibilityState: editorPointer => this.states.activateVisibilityState(editorPointer)
      },
      isBuilderComponentType: compType => this.utilities.isBuilderComponentType(compType),
      isBoxLike: editorPointer => this.isBoxLike(editorPointer)
    };
  }
  getPublicHierarchyAPI() {
    return {
      getChildren: (editorPointer, isRecursive) => this.getChildren(editorPointer, isRecursive),
      getChildrenEditorPointers: editorPointer => this.getChildrenEditorPointers(editorPointer),
      getAncestors: editorPointer => this.getAncestors(editorPointer),
      getActionableElements: (editorPointer, actionName, config) => this.getActionableElements(editorPointer, actionName, config)
    };
  }
}
//# sourceMappingURL=ComponentsService.js.map